import React, { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import { KpiCard, EmptyState } from '../../components/ui'
import { StatusBanner } from '../../components/StatusBanner'
import { ProviderCredentialingPanel } from '../../components/ProviderCredentialingPanel'
import type {
  Provider, CalendarConnection, PracticeMetrics, OrganizationHealth, BillingSummary,
  SessionEvent, Patient, ContinuityAlert,
} from '../../lib/types'

function startOfToday(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

function isToday(iso: string | null): boolean {
  if (!iso) return false
  const d = new Date(iso)
  const today = startOfToday()
  return d >= today && d < new Date(today.getTime() + 24 * 60 * 60 * 1000)
}

/** A Solo Provider org has exactly one provider account — this dashboard
 * has no roster, invite, add-practice, or add-provider controls anywhere,
 * unlike Group Practice/Clinic. Sections match the approved spec exactly:
 * Today's Schedule, My Practice, Continuity, Performance. */
export default function SoloProviderDashboard() {
  const [provider, setProvider] = useState<Provider | null>(null)
  const [connections, setConnections] = useState<CalendarConnection[]>([])
  const [sessions, setSessions] = useState<SessionEvent[]>([])
  const [patients, setPatients] = useState<Patient[]>([])
  const [metrics, setMetrics] = useState<PracticeMetrics | null>(null)
  const [health, setHealth] = useState<OrganizationHealth | null>(null)
  const [alerts, setAlerts] = useState<ContinuityAlert[]>([])
  const [billing, setBilling] = useState<BillingSummary | null>(null)
  const [loading, setLoading] = useState(true)

  async function load() {
    setLoading(true)
    const providers = await api('/providers')
    const me: Provider | undefined = providers[0]
    setProvider(me || null)

    const [conns, sess, pts, m, h, a, b] = await Promise.all([
      me ? api(`/providers/${me.id}/calendar-connections`) : Promise.resolve([]),
      api('/v1/session-events'),
      api('/patients'),
      api('/v1/practice/metrics'),
      api('/v1/practice/health'),
      api('/v1/risk-alerts?status=open'),
      api('/v1/practice/billing-summary'),
    ])
    setConnections(conns); setSessions(sess); setPatients(pts); setMetrics(m); setHealth(h); setAlerts(a); setBilling(b)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  function patientName(id: string) { return patients.find((p) => p.id === id)?.name || 'Patient' }

  if (loading) return <div className="page-loading">Loading your practice…</div>
  if (!provider) return <EmptyState>No provider profile found for this account.</EmptyState>

  const todaysSessions = sessions
    .filter((s) => isToday(s.scheduled_at) && ['scheduled', 'confirmed'].includes(s.status))
    .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime())

  const latestConnection = [...connections].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
  const calendarStatusLabel = !latestConnection
    ? 'Not Connected'
    : latestConnection.status === 'connected'
      ? `Connected${latestConnection.last_synced_at ? ` · Last sync ${new Date(latestConnection.last_synced_at).toLocaleString()}` : ' · Not yet synced'}`
      : 'Not Connected'

  const myRetention = metrics?.provider_retention.find((r) => r.provider_id === provider.id)
  const capacityUtilization = provider.max_active_caseload
    ? Math.round(((metrics?.active_cases ?? 0) / provider.max_active_caseload) * 100)
    : null

  return (
    <div>
      <div className="page-head">
        <h1>{provider.name}</h1>
        <div className="ph-sub">Your solo practice dashboard</div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Today's Schedule</h2></div>
        {todaysSessions.length === 0 ? (
          <EmptyState>No sessions scheduled today.</EmptyState>
        ) : (
          <div className="row-list">
            {todaysSessions.map((s) => (
              <div key={s.id} className="row">
                <span>{patientName(s.patient_id || '')}</span>
                {/* UTC-pinned to match availability_engine.py's slot generation — see
                    BookAppointment.tsx's timeLabel comment for why. */}
                <span className="muted">{new Date(s.scheduled_at!).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' })}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section-block">
        <div className="section-head"><h2>My Practice</h2></div>

        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>Calendar</span>
            <span className={`badge ${latestConnection?.status === 'connected' ? 'badge-green' : 'badge-grey'}`}>{calendarStatusLabel}</span>
          </div>
          {latestConnection?.status !== 'connected' && (
            <div className="warm-theme" style={{ marginTop: 10 }}>
              <div className="card" style={{ padding: '10px 14px' }}>
                <b>Booking Disabled</b>
                <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Connect a calendar to begin receiving patient appointments.</p>
              </div>
            </div>
          )}
        </div>

        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ marginTop: 0 }}>Credentialing &amp; profile</h3>
          <ProviderCredentialingPanel provider={provider} editable onUpdated={load} />
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted">Profile completed</span>
            <span>{provider.profile_completed_at ? 'Yes' : 'Not yet'}</span>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Practice details</h3>
          <div className="row-list">
            <div className="row"><span className="muted">Working days</span><span>{provider.working_days?.length ? provider.working_days.join(', ') : '—'}</span></div>
            <div className="row"><span className="muted">Session length</span><span>{provider.session_duration_minutes ? `${provider.session_duration_minutes} min` : '—'}</span></div>
            <div className="row"><span className="muted">Insurance accepted</span><span>{provider.insurance_accepted?.length ? provider.insurance_accepted.join(', ') : '—'}</span></div>
            <div className="row"><span className="muted">Self-pay fee</span><span>{provider.self_pay_price_cents != null ? `$${(provider.self_pay_price_cents / 100).toFixed(0)} / session` : '—'}</span></div>
            <div className="row"><span className="muted">Capacity</span><span>{provider.capacity_status}</span></div>
            <div className="row"><span className="muted">Office location</span><span>{[provider.city, provider.state_province, provider.country].filter(Boolean).join(', ') || '—'}</span></div>
          </div>
        </div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Continuity</h2></div>
        {health && (
          <StatusBanner
            riskLevel={health.risk_level}
            score={health.health_score}
            components={[
              { label: 'Session 4 rate', value: health.components.session_4_rate != null ? `${Math.round(health.components.session_4_rate * 100)}%` : '—' },
              { label: 'Early dropout rate', value: health.components.early_dropout_rate != null ? `${Math.round(health.components.early_dropout_rate * 100)}%` : '—' },
              { label: 'Open continuity alerts', value: health.components.continuity_alerts_open },
            ]}
          />
        )}
        <div className="section-block">
          <div className="section-head" style={{ marginTop: 12 }}><h2>Drop-off &amp; rebooking alerts</h2></div>
          {alerts.length === 0 ? (
            <EmptyState>No open continuity alerts right now.</EmptyState>
          ) : (
            <div className="row-list">
              {alerts.map((a) => (
                <div key={a.id} className="row">
                  <span>{patientName(a.patient_id)}</span>
                  <span className="muted">{a.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Performance</h2></div>
        <div className="kpis">
          <KpiCard label="Session 4 conversion" value={metrics?.session_4_conversion_rate != null ? `${Math.round(metrics.session_4_conversion_rate * 100)}%` : '—'} />
          <KpiCard label="Average wait time" value={provider.average_wait_time_days != null ? `${provider.average_wait_time_days} days` : '—'} />
          <KpiCard label="Capacity utilization" value={capacityUtilization != null ? `${capacityUtilization}%` : '—'} />
          <KpiCard label="Retention rate" value={myRetention?.retention_rate != null ? `${Math.round(myRetention.retention_rate * 100)}%` : '—'} />
        </div>
        <div className="card" style={{ marginTop: 12 }}>
          <h3 style={{ marginTop: 0 }}>Outcome fee summary</h3>
          {!billing || billing.by_event_type.length === 0 ? (
            <EmptyState>No billing events recorded yet.</EmptyState>
          ) : (
            <div className="row-list">
              {billing.by_event_type.map((row) => (
                <div key={row.event_type} className="row">
                  <span>{row.event_type.replace(/_/g, ' ')} ({row.count})</span>
                  <span className="muted">${(row.total_amount_cents / 100).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
